import root from 'window-or-global';

import { AnalyticsIdentifiers, getSessionState } from 'components/Analytics/google-analytics-store';
import { COOKIE_ANALYTICS, COOKIE_CONSENT } from 'constants/cookies';
import { isCheckoutPath, isPaymentPath } from 'constants/payment-pages';
import { getCookie } from 'utils/cookie';
import { getFeatureFlags } from 'utils/feature-flags';
import locator from 'utils/locator';
import { session } from 'utils/storage';
import { sendGoogleAnalyticsEvent } from './sendGoogleAnalyticsEvent';
import { initialiseContentsquareAdapter } from './contentsquare-ga-adapter';

type WindowWithMonetate = Window & {
  monetateQ?: {
    push: (...args: unknown[]) => void;
  };
};
type WindowWithDataLayer = Window & {
  dataLayer?: unknown[] & {
    parentPush?: (...args: unknown[]) => void;
    originalPush?: (...args: unknown[]) => void;
  };
};

const proxyRoot = root as unknown as WindowWithMonetate & WindowWithDataLayer;

const MonetateSessionStateId = 'wtr-monetateq';

export function isAnalyticsConsented() {
  // Slightly counter intuitively the analytics cookie is inferred as enabled if it is not explicitly disabled ("0").
  // The Android app in particular does not set this cookie and assumes that analytics is always enabled.
  return getCookie(COOKIE_CONSENT) === '1' && getCookie(COOKIE_ANALYTICS) !== '0';
}

function excludeExternal(): boolean {
  const { checkout_excludeExternal: flag } = getFeatureFlags() ?? {};
  return !!flag;
}

function isFunction(value: unknown): value is () => void {
  return typeof value === 'function';
}

function replayMonetateEvents() {
  // We only replay monetate events on on non-payment pages, when we have the monetate script loaded
  if (!isPaymentPath(locator.pathname)) {
    const events = session.getJson<unknown[]>(MonetateSessionStateId);

    // clear the events to avoid rehydrating them again
    session.remove(MonetateSessionStateId);
    if (events?.length && proxyRoot.monetateQ?.push) {
      // send the stashed events to the root monetateQ (avoid the wrapper module)
      events.forEach(event => {
        proxyRoot.monetateQ?.push(event);
      });
    }
  }
}

function addMonetateProxy() {
  // We exclude monetateQ on all payment pages but only capture events on the checkout page
  if (isCheckoutPath(locator.pathname)) {
    // clear any previous events
    session.remove(MonetateSessionStateId);

    // If we have the stubbed monetateQ push function already injected then we must wrap it
    const existingMonetateQPush = proxyRoot.monetateQ?.push?.bind(proxyRoot.monetateQ);

    proxyRoot.monetateQ = {
      push: function recordMonetateEvent(...args: unknown[]) {
        const events = session.getJson<unknown[]>(MonetateSessionStateId) ?? [];
        session.setJson(MonetateSessionStateId, [...events, ...args]);

        // call the original monetateQ push function, if it exists (to allow logging from browser extensions)
        existingMonetateQPush?.(...args);
      },
    };
  }
}

function getGoogleAnalyticsIDs(): AnalyticsIdentifiers {
  let clientId: undefined | string;
  let sessionId: undefined | string;

  // Mobile Apps should pass the Waitrose Customer ID and GA sessionId in the URL
  // SS GTM will join mobile app FireStore sessions with the web GA session using the
  // customer ID and session ID.
  // The ga_cid is not really a client ID (as apps do not have one of these), but is unique per customer.
  const queryParams = new URLSearchParams(locator.search);
  clientId = queryParams.get('ga_cid') || undefined;
  sessionId = queryParams.get('ga_session_id') || undefined;

  // If not passed in the URL, try to get them from the session state
  if (!clientId || !sessionId) {
    const gaSettings = getSessionState();
    ({ clientId, sessionId } = gaSettings);
  }
  return { clientId, sessionId };
}

function addGoogleAnalyticsProxy({ clientId, sessionId }: AnalyticsIdentifiers) {
  if (!clientId || !sessionId) {
    return;
  }

  if (!proxyRoot?.dataLayer?.parentPush) {
    proxyRoot.dataLayer ??= [] as unknown as WindowWithDataLayer['dataLayer'];
    const parentPush = proxyRoot.dataLayer.push;
    proxyRoot.dataLayer.parentPush = parentPush;

    proxyRoot.dataLayer.push = (...args: unknown[]) => {
      // call the original dataLayer push function, if it exists (to allow logging from browser extensions)
      const result = parentPush.apply(proxyRoot.dataLayer, args);

      sendGoogleAnalyticsEvent(clientId, sessionId, args);

      return result;
    };

    // If the existing dataLayer push function has events already, we need to send them
    if (Array.isArray(proxyRoot.dataLayer) && proxyRoot.dataLayer.length > 0) {
      proxyRoot.dataLayer.forEach(event => {
        if (event && isFunction(event)) {
          // If the event is a function, we need to call it to match GTM dataLayer behaviour
          event();
          return;
        }
        sendGoogleAnalyticsEvent(clientId, sessionId, [event]);
      });
    }
  }
}

export function addThirdpartyScriptProxies() {
  if (__SERVER__ || !excludeExternal()) {
    return;
  }

  replayMonetateEvents();
  addMonetateProxy();

  // Proxy is only used in payment pages when we also do not have GTM injected
  if (!isPaymentPath(locator.pathname) || !!proxyRoot.gtag) {
    return;
  }

  const cookieAnalytics = isAnalyticsConsented();

  // If we don't have consent, we don't need to proxy the dataLayer or Contentsquare events
  if (!cookieAnalytics) {
    return;
  }

  const analyticsIdentifiers = getGoogleAnalyticsIDs();
  initialiseContentsquareAdapter(analyticsIdentifiers);
  addGoogleAnalyticsProxy(analyticsIdentifiers);
}
